// .ts extension: these modules are in the node --test import graph, which resolves literally
import type { QuestionBankPayload } from './payload.ts';
import type {
  QuestionBankItem,
  QuestionBankLessonRef,
  QuestionBankLevel,
  QuestionBankOption,
  QuestionBankSource,
  QuestionBankType,
} from './types.ts';

/**
 * Board question <-> IZTeach question, both ways, and nothing else.
 *
 * The IZ shape is izteach-core-alternative-model model/question/schema.js as the admin's
 * /question-bank writes it: `html` is the stem, `answers` is a map of key -> { key, label, type,
 * value }, `correct` is the key (single-choice) or a key list (multiple-choice) the grader in
 * examination-service submission/services.js compares against, and the explanation lives in
 * `config.explain`. Facts only the board has - source, attached lecture, Đúng - Sai - ride in
 * `config.msb`; the admin spreads config on save, so its edits keep them.
 */

export interface IzAnswer {
  key: string;
  value: string;
  label?: string;
  type?: string;
}

/** The fields of an IZ question the board reads. Everything else is left to the server. */
export interface IzQuestion {
  id?: string;
  _id?: string;
  type: string;
  html?: string;
  title?: string;
  level?: number;
  answers?: Record<string, IzAnswer> | IzAnswer[] | null;
  correct?: unknown;
  config?: Record<string, unknown> | null;
  /** Group only: child question ids (or populated children on GET /question/{id}). */
  questions?: unknown;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** What POST /question and PUT /question/{id} are sent. vendorId is added by the repository. */
export interface IzQuestionBody {
  type: string;
  html: string;
  title: string;
  level: number;
  answers: Record<string, IzAnswer>;
  correct?: string | string[];
  config: Record<string, unknown>;
}

/** The board-only facts kept under config.msb. */
export interface IzBoardMeta {
  source?: QuestionBankSource;
  lessonRef?: QuestionBankLessonRef;
  /** Đúng - Sai is stored as a two-answer single-choice so IZ exams grade it; this marks it. */
  type?: 'boolean';
}

/** IZ types the board can show and edit. Fill-blank, group and true-false-choice are view-only (viewOnly.ts). */
const BOARD_IZ_TYPES = ['single-choice', 'multiple-choice', 'text'];

const SOURCES: QuestionBankSource[] = ['ai-quiz-supporter', 'manual'];

/** IZ titles feed a search tag, so a stem that is all markup still needs a name. */
const FALLBACK_TITLE = 'Câu hỏi';
const TITLE_LIMIT = 255;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Answers in stored order; an array shape is tolerated because the schema types them Mixed. */
export function answerList(answers: IzQuestion['answers']): IzAnswer[] {
  if (!answers) return [];
  // a map entry that lost its own key still has the one it is filed under
  const list: unknown[] = Array.isArray(answers)
    ? answers
    : Object.keys(answers).map((key) => (isRecord(answers[key]) ? { ...answers[key], key: answers[key].key || key } : null));
  const out: IzAnswer[] = [];
  list.forEach((entry) => {
    if (!isRecord(entry)) return;
    const key = asString(entry.key);
    if (!key) return;
    out.push({ key, value: asString(entry.value), label: asString(entry.label) || undefined, type: asString(entry.type) || undefined });
  });
  return out;
}

/** `correct` is a key, a key list, or (older rows) a list of { key }. */
export function correctKeys(correct: unknown): string[] {
  if (typeof correct === 'string') return correct ? [correct] : [];
  if (!Array.isArray(correct)) return [];
  return correct
    .map((entry) => (typeof entry === 'string' ? entry : isRecord(entry) ? asString(entry.key) : ''))
    .filter((key) => key !== '');
}

/** IZ levels run 1-4 (NB, TH, VD, VDC); the board has three, so VDC reads as Khó. */
export function toBoardLevel(level: unknown): QuestionBankLevel {
  const value = Math.round(Number(level));
  if (!Number.isFinite(value) || value <= 1) return 1;
  return value >= 3 ? 3 : 2;
}

function readLessonRef(value: unknown): QuestionBankLessonRef | undefined {
  if (!isRecord(value)) return undefined;
  const courseId = asString(value.courseId);
  const unitId = asString(value.unitId);
  if (!courseId || !unitId) return undefined;
  return { courseId, unitId, unitName: asString(value.unitName) };
}

/** The IZ question as a board row, or null when it is a type the board cannot show. */
export function fromIzQuestion(raw: IzQuestion): QuestionBankItem | null {
  const id = asString(raw.id) || asString(raw._id);
  if (!id || BOARD_IZ_TYPES.indexOf(raw.type) === -1) return null;
  const config = isRecord(raw.config) ? raw.config : {};
  const meta = isRecord(config.msb) ? config.msb : {};
  const correct = correctKeys(raw.correct);
  const options: QuestionBankOption[] = raw.type === 'text'
    ? []
    : answerList(raw.answers).map((answer) => ({ key: answer.key, content: answer.value, isCorrect: correct.indexOf(answer.key) >= 0 }));
  const type: QuestionBankType = raw.type === 'single-choice' && meta.type === 'boolean' && options.length === 2
    ? 'boolean'
    : (raw.type as QuestionBankType);
  const source = SOURCES.filter((value) => value === meta.source)[0] || 'manual';
  const item: QuestionBankItem = {
    id,
    content: asString(raw.html),
    type,
    level: toBoardLevel(raw.level),
    options,
    source,
    izConfig: config,
    createdAt: asString(raw.createdAt),
    updatedAt: asString(raw.updatedAt),
  };
  const explanation = asString(config.explain);
  if (explanation) item.explanation = explanation;
  const lessonRef = readLessonRef(meta.lessonRef);
  if (lessonRef) item.lessonRef = lessonRef;
  if (raw.createdBy) item.createdBy = String(raw.createdBy);
  return item;
}

/** Plain text of the stem, for the IZ `title` the admin list and its search tags read. */
export function plainTitle(html: string): string {
  const textOnly = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
  return textOnly.slice(0, TITLE_LIMIT) || FALLBACK_TITLE;
}

/** A v4-shaped key like the admin's; Math.random because crypto.randomUUID is not on Chrome 58. */
export function randomAnswerKey(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const nibble = Math.floor(Math.random() * 16);
    return (char === 'x' ? nibble : (nibble & 0x3) | 0x8).toString(16);
  });
}

/**
 * The IZ document for a validated payload.
 *
 * `previousConfig` is the stored config of the question being edited: config is replaced whole on
 * PUT, so the admin's own keys have to be sent back. Answer keys already on the options are kept;
 * new options get a fresh one from `newKey`.
 */
export function toIzQuestion(
  payload: QuestionBankPayload,
  previousConfig: Record<string, unknown> | undefined,
  newKey: () => string = randomAnswerKey,
): IzQuestionBody {
  const answers: Record<string, IzAnswer> = {};
  const correct: string[] = [];
  payload.options.forEach((option, index) => {
    let key = option.key || '';
    // two options can never share a key, whatever the editor handed over
    while (!key || answers[key]) key = newKey();
    answers[key] = { key, label: String.fromCharCode(65 + index), type: 'html', value: option.content };
    if (option.isCorrect) correct.push(key);
  });

  const meta: IzBoardMeta = { source: payload.source };
  if (payload.lessonRef) meta.lessonRef = payload.lessonRef;
  if (payload.type === 'boolean') meta.type = 'boolean';

  const body: IzQuestionBody = {
    type: payload.type === 'boolean' ? 'single-choice' : payload.type,
    html: payload.content,
    title: plainTitle(payload.content),
    level: payload.level,
    answers,
    config: { ...(previousConfig || {}), explain: payload.explanation, isShowExplain: payload.explanation !== '', msb: meta },
  };
  if (payload.type === 'multiple-choice') body.correct = correct;
  else if (payload.type !== 'text') body.correct = correct[0] || '';
  return body;
}
